import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlertCircle, CheckCircle2 } from 'lucide-react';
import { apiService } from '../../services/api';
import { useAuth } from '../../hooks/useAuth';

type ConfirmStatus = 'confirming' | 'success' | 'alreadyApplied' | 'failed';

// Mirror of VerifyEmailPage but for email-change confirmation. Reads the token
// from ?token=..., POSTs it to /api/Auth/confirm-email-change, and tells the
// user what happened. After a fresh success the backend has revoked all refresh
// tokens, so the user must sign in again with the new address.
const ConfirmEmailChangePage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { clearAuthState } = useAuth();

  const [status, setStatus] = useState<ConfirmStatus>('confirming');
  const [message, setMessage] = useState('');

  const hasCalledRef = useRef(false);

  // Restore any "+" chars that URL decoding turned into spaces.
  const fixTokenFromUrl = (token: string) => token.replace(/\s/g, '+');

  useEffect(() => {
    if (hasCalledRef.current) return;
    hasCalledRef.current = true;

    const rawToken = searchParams.get('token');
    if (!rawToken) {
      setStatus('failed');
      setMessage('Confirmation link is missing or invalid.');
      return;
    }

    const token = fixTokenFromUrl(rawToken);

    (async () => {
      try {
        const response = await apiService.confirmEmailChange(token);
        if (response.success) {
          const backendMessage = (response.message || '').toLowerCase();
          const isAlready = backendMessage.includes('already');

          // Server-side the TokenVersion has already been bumped and the cookies
          // were expired in the confirm response, so the old access token is dead.
          // clearAuthState() tears down the React context + localStorage in one
          // shot WITHOUT any API call — so when we navigate to /login below,
          // LoginPage sees isAuthenticated=false immediately and doesn't briefly
          // bounce us to /devices. (Calling logout() here would 401 against the
          // stale token and trip the interceptor's refresh-then-redirect cascade,
          // which is exactly what caused the home-screen flash.)
          clearAuthState();

          setStatus(isAlready ? 'alreadyApplied' : 'success');
          setMessage(response.message || '');
          if (!isAlready) {
            setTimeout(() => navigate('/login'), 3500);
          }
        } else {
          setStatus('failed');
          setMessage(response.error || 'This confirmation link is invalid or has expired.');
        }
      } catch (error) {
        setStatus('failed');
        setMessage(error instanceof Error ? error.message : 'Unable to confirm the email change. Please try again.');
      }
    })();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen h-full flex items-center justify-center relative">
      <picture className="absolute inset-0 z-0">
        <source media="(max-width: 480px)" srcSet={`${process.env.PUBLIC_URL}/SVGIcons/login-bg-mobile.webp`} />
        <source media="(max-width: 920px)" srcSet={`${process.env.PUBLIC_URL}/SVGIcons/login-bg-tablet.webp`} />
        <img
          src={`${process.env.PUBLIC_URL}/SVGIcons/login-bg-desktop.webp`}
          alt=""
          className="w-full h-full object-cover"
        />
      </picture>

      <div className="relative z-10 w-full max-w-md mx-4 bg-[var(--modal-background)] rounded-lg shadow-xl p-8 text-center zoomOutLevel">
        {status === 'confirming' && (
          <>
            <div className="mx-auto mb-4 w-20 h-20 rounded-full flex items-center justify-center bg-gradient-to-b from-[#0F796B] to-[#6FDC95]">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-white"></div>
            </div>
            <h3 className="text-xl font-semibold text-[var(--color-text)] mb-2">Confirming your new email…</h3>
            <p className="text-base font-light text-[var(--color-sub-title-text)]">
              Please wait while we finish updating your SmartEco account.
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="mx-auto mb-4 w-20 h-20 rounded-full flex items-center justify-center bg-gradient-to-b from-[#0F796B] to-[#6FDC95]">
              <CheckCircle2 className="h-10 w-10 text-[var(--modal-background)]" />
            </div>
            <h3 className="sm:text-2xl text-xl font-light text-[var(--color-text)] mb-1">
              Email updated
            </h3>
            <p className="text-[var(--color-sub-title-text)] font-normal sm:text-sm text-xs mb-4">
              {message || 'Your sign-in email has been updated. Please sign in again with your new address.'}
            </p>
            <button
              onClick={() => navigate('/login')}
              className="add-device-save-btn"
            >
              Sign In
            </button>
          </>
        )}

        {status === 'alreadyApplied' && (
          <>
            <div className="mx-auto mb-4 w-20 h-20 rounded-full flex items-center justify-center bg-gradient-to-b from-[#0F796B] to-[#6FDC95]">
              <CheckCircle2 className="h-10 w-10 text-[var(--modal-background)]" />
            </div>
            <h3 className="sm:text-2xl text-xl font-light text-[var(--color-text)] mb-1">
              Already updated
            </h3>
            <p className="text-[var(--color-sub-title-text)] font-normal sm:text-sm text-xs mb-4">
              {message || 'Your email has already been updated. Please sign in with your new address.'}
            </p>
            <button
              onClick={() => navigate('/login')}
              className="add-device-save-btn"
            >
              Sign In
            </button>
          </>
        )}

        {status === 'failed' && (
          <>
            <div className="mx-auto mb-4 w-20 h-20 rounded-full flex items-center justify-center bg-gradient-to-r from-[#d92b1e] to-[#b04a43]">
              <AlertCircle className="h-10 w-10 text-[var(--modal-background)]" />
            </div>
            <h3 className="sm:text-2xl text-xl font-light text-[var(--color-text)] mb-1">Confirmation Failed</h3>
            <p className="text-[var(--color-sub-title-text)] font-normal sm:text-sm text-xs mb-4">{message}</p>
            <p className="text-[var(--color-sub-title-text)] font-light sm:text-sm text-xs mb-4">
              Your sign-in email has not been changed. You can start the change again from your account settings.
            </p>
            <div className="flex flex-col gap-3 mt-4">
              <button
                onClick={() => navigate('/login')}
                className="add-device-save-btn"
              >
                Back to Sign In
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ConfirmEmailChangePage;
